import { z } from "zod";
import type { InvoiceParty } from "@remi/enums/invoice-party";
import { InvoicePartySchema } from "@remi/enums/invoice-party";
import { SowChangeType } from "@remi/enums/sow-change-type";

import type { BidLineItemOutput } from "./quoting-v3";
import { BidLineItemOutputSchema } from "./quoting-v3";
import { SowChangeBlockedProjectRequestSchema } from "./sow-change/blocked-project";

// Optional so the legacy path and the activity logs that reuse this schema keep parsing without it.
const blockedProjects = z.array(SowChangeBlockedProjectRequestSchema).optional();

export const requestSowChangeInputSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(SowChangeType.panelCount),
    bidId: z.number(),
    blockedProjects,
    pvSystems: z.array(
      z.object({
        id: z.string(),
        removalPanels: z.number(),
        reinstallPanels: z.number(),
      }),
    ),
  }),
  z.object({
    type: z.literal(SowChangeType.newQuote),
    bidId: z.number(),
    blockedProjects,
    sowChanges: z.string(),
  }),
  z.object({
    type: z.literal(SowChangeType.existingQuote),
    bidId: z.number(),
    blockedProjects,
    selectedQuoteId: z.string(),
    sowChanges: z.string().optional(),
  }),
  z.object({
    type: z.literal(SowChangeType.lineItems),
    bidId: z.number(),
    blockedProjects,
    sowChanges: z.string().min(1, "Description is required").max(2000),
    requestForQuoteId: z.number().int().positive().optional(),
    lineItems: z
      .array(
        BidLineItemOutputSchema.omit({ lineItemType: true, pvSystem: true, pvSystemName: true }).extend({
          quantity: z.number().min(0.01).max(999999),
          invoiceParty: InvoicePartySchema.default("HOMEOWNER"),
          projectId: z.number().nullish(),
          changeOrderId: z.string().nullish(),
        }),
      )
      .min(1),
  }),
  z.object({
    type: z.literal(SowChangeType.supplement),
    bidId: z.number(),
    blockedProjects,
    sowChanges: z.string(),
  }),
  z.object({
    type: z.literal(SowChangeType.elective),
    bidId: z.number(),
    blockedProjects,
    sowChanges: z.string(),
  }),
  z.object({
    type: z.literal(SowChangeType.supplementAndElective),
    bidId: z.number(),
    blockedProjects,
    sowChanges: z.string(),
  }),
  z.object({
    type: z.literal(SowChangeType.insuranceFinancialAdjustment),
    bidId: z.number(),
    blockedProjects,
    deductible: z.number().min(0),
    nonRecoverableDepreciation: z.number().min(0),
    insurancePriceAdjustment: z.number(),
    sowChanges: z.string(),
  }),
]);

export type RequestSowChangeInput = z.infer<typeof requestSowChangeInputSchema>;

export interface RequestSowChangeOptions {
  assignSendAgreementToSubmitter?: boolean;
}

/**
 * Line item state used in SOW change forms.
 * Extends BidLineItemOutput with an id and projectId (needed for React state management).
 * Removes pvSystem object since we only need pvSystemId.
 */
export type SowChangeLineItemState = Omit<BidLineItemOutput, "pvSystem"> & {
  id: string;
  projectId: number | null;
};

/**
 * Simplified line item type for display/editing in SelectedLineItemRow.
 * Contains only the fields needed for editing individual line items.
 */
export type SowChangeLineItemDisplay = {
  id: string;
  lineItemTypeName: string;
  pricingType: string;
  unitPrice: number;
  quantity: number;
  visibleOnQuote: boolean;
  shouldInvoice: boolean;
  invoiceParty: InvoiceParty;
  customName: string | null;
  customDescription: string | null;
  projectId: number | null;
};
